import { type DraftStore } from "../store-contract";
import { type DraftSidecar, type EvidenceKind } from "../types";
import { compareStrings, getAllRows, getRow, txDone, updateRow } from "./db";

// Draft answer documents + staged-evidence bookkeeping for in-progress
// captures, transcribed from the device store's SQL. Locked drafts are sealed
// (the enqueue transaction locks them) and immutable until cleanup deletes
// them; debounce lives in the renderer hook, not here.

// The device store's `locked` INTEGER 0/1 lands as a native boolean, the
// review_required precedent in bundle.ts.
interface DraftRow {
  assignment_id: string;
  answers_json: string;
  locked: boolean;
  updated_at: number;
  test_kit_id: string | null;
}

interface DraftSidecarRow {
  key: string;
  sidecar_json: string;
}

interface DraftAttachmentStoreRow {
  client_attachment_id: string;
  assignment_id: string;
  link_id: string;
  kind: EvidenceKind;
  file_uri: string;
  content_type: string;
  file_name: string;
  staged_at: string;
}

export function createDraftStore(db: IDBDatabase): DraftStore {
  const readStore = (name: string): IDBObjectStore =>
    db.transaction(name, "readonly").objectStore(name);

  return {
    // The device store pairs `UPDATE … WHERE locked = 0` with
    // `INSERT OR IGNORE` on a zero-change fallback: update only an unlocked
    // row, insert only when no row exists, and leave a locked (sealed) row
    // untouched — a stale tab can never write into a draft its sibling sealed.
    async saveDraft(assignmentId, answers, now = Date.now()) {
      const answersJson = JSON.stringify(answers);
      const tx = db.transaction("drafts", "readwrite");
      const store = tx.objectStore("drafts");
      const row = await getRow<DraftRow>(store, assignmentId);
      if (row === null) {
        store.put({
          assignment_id: assignmentId,
          answers_json: answersJson,
          locked: false,
          updated_at: now,
          test_kit_id: null,
        } satisfies DraftRow);
      } else if (!row.locked) {
        row.answers_json = answersJson;
        row.updated_at = now;
        store.put(row);
      }
      await txDone(tx);
    },

    async getDraft(assignmentId) {
      const row = await getRow<DraftRow>(readStore("drafts"), assignmentId);
      if (row === null) return null;
      return {
        assignmentId,
        // answers_json was written as JSON.stringify of the answer document.
        answers: JSON.parse(row.answers_json) as Record<string, unknown>,
        locked: row.locked,
        updatedAt: row.updated_at,
        testKitId: row.test_kit_id,
      };
    },

    // Records the chosen test kit on a draft (the finalize-time picker's
    // offline write). A LOCKED (sealed) draft no-ops — sealed drafts are
    // immutable; null clears the selection. Creates the draft row when none
    // exists yet, mirroring saveDraft's insert-on-miss shape.
    async setDraftKit(assignmentId, testKitId) {
      const tx = db.transaction("drafts", "readwrite");
      const store = tx.objectStore("drafts");
      const row = await getRow<DraftRow>(store, assignmentId);
      if (row === null) {
        store.put({
          assignment_id: assignmentId,
          answers_json: "{}",
          locked: false,
          updated_at: Date.now(),
          test_kit_id: testKitId,
        } satisfies DraftRow);
      } else if (!row.locked) {
        row.test_kit_id = testKitId;
        store.put(row);
      }
      await txDone(tx);
    },

    async lockDraft(assignmentId) {
      await updateRow<DraftRow>(db, "drafts", assignmentId, (row) => {
        row.locked = true;
        return true;
      });
    },

    // Removes the draft and its attachment bookkeeping rows. The staged BYTES
    // are the evidence store's cleanup concern — this only drops rows.
    async deleteDraft(assignmentId) {
      const tx = db.transaction(["drafts", "draft_attachments"], "readwrite");
      tx.objectStore("drafts").delete(assignmentId);
      const attachments = tx.objectStore("draft_attachments");
      const rows = await getAllRows<DraftAttachmentStoreRow>(
        attachments.index("assignment_id"),
        assignmentId,
      );
      for (const r of rows) {
        attachments.delete(r.client_attachment_id);
      }
      await txDone(tx);
    },

    // put is the INSERT OR REPLACE twin: re-staging the same attachment id
    // replaces its row.
    async stageDraftAttachment(row) {
      const tx = db.transaction("draft_attachments", "readwrite");
      tx.objectStore("draft_attachments").put({
        client_attachment_id: row.clientAttachmentId,
        assignment_id: row.assignmentId,
        link_id: row.linkId,
        kind: row.kind,
        file_uri: row.locator,
        content_type: row.contentType,
        file_name: row.fileName,
        staged_at: row.stagedAt,
      } satisfies DraftAttachmentStoreRow);
      await txDone(tx);
    },

    async listDraftAttachments(assignmentId) {
      const rows = await getAllRows<DraftAttachmentStoreRow>(
        readStore("draft_attachments").index("assignment_id"),
        assignmentId,
      );
      // ORDER BY client_attachment_id ASC — UUIDv7 mint order.
      rows.sort((x, y) => compareStrings(x.client_attachment_id, y.client_attachment_id));
      return rows.map((r) => ({
        clientAttachmentId: r.client_attachment_id,
        assignmentId: r.assignment_id,
        linkId: r.link_id,
        kind: r.kind,
        locator: r.file_uri,
        contentType: r.content_type,
        fileName: r.file_name,
        stagedAt: r.staged_at,
      }));
    },

    async removeDraftAttachment(clientAttachmentId) {
      const tx = db.transaction("draft_attachments", "readwrite");
      tx.objectStore("draft_attachments").delete(clientAttachmentId);
      await txDone(tx);
    },

    async saveDraftSidecar(key, sidecar) {
      const tx = db.transaction("draft_sidecars", "readwrite");
      tx.objectStore("draft_sidecars").put({
        key,
        sidecar_json: JSON.stringify(sidecar),
      } satisfies DraftSidecarRow);
      await txDone(tx);
    },

    async loadDraftSidecar(key) {
      const row = await getRow<DraftSidecarRow>(readStore("draft_sidecars"), key);
      // sidecar_json was written as JSON.stringify of a DraftSidecar.
      return row === null ? null : (JSON.parse(row.sidecar_json) as DraftSidecar);
    },

    async listDraftSidecars() {
      const rows = await getAllRows<DraftSidecarRow>(readStore("draft_sidecars"));
      rows.sort((x, y) => compareStrings(x.key, y.key));
      return rows.map((r) => ({ key: r.key, sidecar: JSON.parse(r.sidecar_json) as DraftSidecar }));
    },

    async deleteDraftSidecar(key) {
      const tx = db.transaction("draft_sidecars", "readwrite");
      tx.objectStore("draft_sidecars").delete(key);
      await txDone(tx);
    },
  };
}
